import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../components/apiconfig/api-config';

const ProtectedRoute = ({ children, requireRole = null, restrictRole = null }) => {
  const [authChecked, setAuthChecked] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hasRequiredRole, setHasRequiredRole] = useState(true);
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        await axios.get(`${API_BASE_URL}/api/auth/protected`, {
          withCredentials: true,
        });
        setIsAuthenticated(true);

        // Check role if required or restricted
        if (requireRole || restrictRole) {
          const response = await axios.get(
            `${API_BASE_URL}/api/profile/profile-get`,
            { withCredentials: true }
          );
          if (response.data.success) {
            const role = response.data.data.role;
            setUserRole(role);
            
            if (requireRole) {
              setHasRequiredRole(role === requireRole);
            }
          }
        }
      } catch (err) {
        // If unauthorized, try to refresh token
        if (err.response && (err.response.status === 401 || err.response.status === 403)) {
          try {
            await axios.post(`${API_BASE_URL}/api/auth/refresh-token`, {}, { withCredentials: true });
            // Try again after refresh
            await axios.get(`${API_BASE_URL}/api/auth/protected`, { withCredentials: true });
            setIsAuthenticated(true);

            // Check role again after refresh
            if (requireRole || restrictRole) {
              const response = await axios.get(
                `${API_BASE_URL}/api/profile/profile-get`,
                { withCredentials: true }
              );
              if (response.data.success) {
                const role = response.data.data.role;
                setUserRole(role);
                
                if (requireRole) {
                  setHasRequiredRole(role === requireRole);
                }
              }
            }
            return;
          } catch {
            setIsAuthenticated(false);
          }
        } else {
          setIsAuthenticated(false);
        }
      } finally {
        setAuthChecked(true);
      }
    };

    checkAuth();
  }, [requireRole, restrictRole]);

  // Only render loading or redirect, never children until authChecked is true
  if (!authChecked) {
    return <div className="text-center mt-20 text-blue-600">Checking authentication...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // Check role requirement
  if (requireRole && !hasRequiredRole) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="max-w-md w-full mx-4 bg-white rounded shadow-md p-8 text-center">
          <h2 className="text-2xl font-semibold text-gray-800 mb-3">Access Denied</h2>
          <p className="text-gray-600 mb-6">
            You don't have permission to access this page. This page requires <span className="font-medium capitalize">{requireRole}</span> role.
          </p>
          <a 
            href="/dashboard" 
            className="inline-block bg-[#2691cc] text-white px-6 py-2 rounded hover:bg-[#1e7ba8] transition-colors"
          >
            Back to Dashboard
          </a>
        </div>
      </div>
    );
  }

  // Check role restriction
  if (restrictRole && userRole === restrictRole) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="max-w-md w-full mx-4 bg-white rounded shadow-md p-8 text-center">
          <h2 className="text-2xl font-semibold text-gray-800 mb-3">Access Denied</h2>
          <p className="text-gray-600 mb-6">
            You don't have permission to access this page. This page is restricted for <span className="font-medium capitalize">{restrictRole}</span> role.
          </p>
          <a 
            href="/dashboard" 
            className="inline-block bg-[#2691cc] text-white px-6 py-2 rounded hover:bg-[#1e7ba8] transition-colors"
          >
            Back to Dashboard
          </a>
        </div>
      </div>
    );
  }

  return children;
};

export default ProtectedRoute;
